import { cn } from "@/lib/utils";
import { useCanAccessMenu } from "@/hooks/use-menu-permissions";
import { REPORT_METRICS } from "@/lib/reports/registry";
import type { MetricDefinition } from "@/lib/reports/types";

const CATEGORY_LABELS: Record<MetricDefinition["category"], string> = {
  ansatte: "Ansatte",
  okonomi: "Økonomi",
  prosjekt: "Prosjekt",
};

// Visningsrekkefølge på kategori-overskriftene.
const CATEGORY_ORDER: MetricDefinition["category"][] = ["ansatte", "okonomi", "prosjekt"];

/**
 * Left rail that lists the report metrics the active user may access.
 *
 * RBAC: each metric declares `rbac` as a MenuKey (all 5 share "rapporter").
 * We reuse `useCanAccessMenu` — the same role_menu_permissions check that
 * gates the `/rapporter` route in App.tsx — so menu access stays consistent.
 */
export function ReportMenu({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  // All current metrics share `rbac: "rapporter"`; gate the whole list on it.
  // Hook is called unconditionally to satisfy rules-of-hooks.
  const canAccessReports = useCanAccessMenu("rapporter");

  const visible = REPORT_METRICS.filter((m) =>
    m.rbac === "rapporter" ? canAccessReports : false,
  );

  // Grupper metrikkene etter category (Ansatte / Økonomi / Prosjekt) i fast
  // visningsrekkefølge, og dropp tomme kategorier.
  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    metrics: visible.filter((m) => m.category === category),
  })).filter((g) => g.metrics.length > 0);

  return (
    <nav className="flex flex-col gap-1">
      {groups.map(({ category, metrics }) => (
        <div key={category} className="flex flex-col gap-1">
          <span className="mt-3 px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground first:mt-0">
            {CATEGORY_LABELS[category]}
          </span>
          {metrics.map((metric) => {
            const active = metric.id === selectedId;
            return (
              <button
                key={metric.id}
                type="button"
                onClick={() => onSelect(metric.id)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "rounded-md px-3 py-2 text-left text-sm font-medium transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted/50",
                )}
              >
                {metric.label}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
